import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MessageService, ConfirmationService } from 'primeng/api';

import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { CheckboxModule } from 'primeng/checkbox';
import { ApiService, Company } from '../../core/api.service';
import { environment } from '../../../environments/environment';
import { TagModule } from 'primeng/tag';

@Component({
    selector: 'app-companies',
    standalone: true,
    imports: [CommonModule, FormsModule, TableModule, ButtonModule, InputTextModule, DialogModule, ToastModule, ConfirmDialogModule, TagModule, CheckboxModule],
    providers: [MessageService, ConfirmationService],
    template: `
        <p-toast></p-toast>
        <p-confirmDialog></p-confirmDialog>

        <div class="card">
            <div class="flex justify-between items-center mb-4">
                <div class="font-semibold text-xl"><i class="pi pi-building mr-2"></i>Companies</div>
                <p-button label="Add Company" icon="pi pi-plus" (onClick)="openNew()"></p-button>
            </div>

            <p-table [value]="companies" [loading]="loading" [paginator]="true" [rows]="10" styleClass="p-datatable-sm p-datatable-striped">
                <ng-template pTemplate="header">
                    <tr>
                        <th style="width:6rem">Logo</th>
                        <th>Name</th>
                        <th>City</th>
                        <th>Phone</th>
                        <th>Email</th>
                        <th style="width:8rem">Status</th>
                        <th style="width:11rem">Actions</th>
                    </tr>
                </ng-template>
                <ng-template pTemplate="body" let-c>
                    <tr>
                        <td>
                            <img *ngIf="c.logoPath" [src]="logoUrl(c.logoPath)" alt="logo" style="height:36px; max-width:60px; object-fit:contain;" />
                            <span *ngIf="!c.logoPath" class="text-muted-color text-sm">—</span>
                        </td>
                        <td>{{ c.name }}</td>
                        <td>{{ c.city }}</td>
                        <td>{{ c.phone }}</td>
                        <td>{{ c.email }}</td>
                        <td>
                            <p-tag [value]="c.isActive ? 'Active' : 'Inactive'" [severity]="c.isActive ? 'success' : 'secondary'"></p-tag>
                        </td>
                        <td>
                            <p-button icon="pi pi-image" severity="help" [text]="true" (onClick)="openLogo(c)" pTooltip="Logo"></p-button>
                            <p-button icon="pi pi-pencil" severity="info" [text]="true" (onClick)="openEdit(c)"></p-button>
                            <p-button *ngIf="c.isActive" icon="pi pi-ban" severity="danger" [text]="true" (onClick)="confirmDelete(c)" pTooltip="Deactivate"></p-button>
                            <p-button *ngIf="!c.isActive" icon="pi pi-check-circle" severity="success" [text]="true" (onClick)="activate(c)" pTooltip="Activate"></p-button>
                        </td>
                    </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                    <tr>
                        <td colspan="6" class="text-center p-6 text-muted-color">No companies. Add one.</td>
                    </tr>
                </ng-template>
            </p-table>
        </div>

        <!-- Add / Edit dialog -->
        <p-dialog [header]="editId ? 'Edit Company' : 'Add Company'" [(visible)]="dialog" [modal]="true" [style]="{ width: '500px' }" [draggable]="false">
            <div class="flex flex-col gap-4">
                <div class="flex flex-col gap-2"><label class="text-sm font-medium">Name *</label><input pInputText [(ngModel)]="item.name" /></div>
                <div class="flex flex-col gap-2"><label class="text-sm font-medium">Address</label><input pInputText [(ngModel)]="item.address" /></div>
                <div class="flex flex-col gap-2"><label class="text-sm font-medium">City</label><input pInputText [(ngModel)]="item.city" /></div>
                <div class="flex flex-col gap-2"><label class="text-sm font-medium">Phone</label><input pInputText [(ngModel)]="item.phone" /></div>
                <div class="flex flex-col gap-2"><label class="text-sm font-medium">Email</label><input pInputText [(ngModel)]="item.email" /></div>
                <div class="flex flex-col gap-2"><label class="text-sm font-medium">NTN</label><input pInputText [(ngModel)]="item.ntn" /></div>
                <div class="flex flex-col gap-2"><label class="text-sm font-medium">STRN</label><input pInputText [(ngModel)]="item.strn" /></div>
                <div class="flex items-center gap-2">
                    <p-checkbox [(ngModel)]="item.hasSignature" [binary]="true" inputId="hasSig"></p-checkbox>
                    <label for="hasSig" class="text-sm font-medium">Show signature & stamp on invoice</label>
                </div>
                <div class="flex items-center gap-2">
                    <p-checkbox [(ngModel)]="item.showDnHeader" [binary]="true" inputId="showHeader"></p-checkbox>
                    <label for="showHeader" class="text-sm font-medium">Show logo & company name on Delivery Note print</label>
                </div>
            </div>
            <ng-template pTemplate="footer">
                <p-button label="Cancel" severity="secondary" [text]="true" (onClick)="dialog = false"></p-button>
                <p-button label="Save" icon="pi pi-check" (onClick)="save()"></p-button>
            </ng-template>
        </p-dialog>

        <!-- Logo dialog -->
        <p-dialog header="Company Images" [(visible)]="logoDialog" [modal]="true" [style]="{ width: '500px' }" [draggable]="false">
            <div class="flex flex-col gap-5">
                <!-- LOGO -->
                <div class="flex flex-col gap-2 items-center border-b pb-4">
                    <div class="font-medium text-sm self-start">Company Logo</div>
                    <img *ngIf="logoCompany?.logoPath" [src]="logoUrl(logoCompany!.logoPath!)" style="max-height:80px; object-fit:contain; border:1px solid #ddd; padding:6px; border-radius:8px;" />
                    <div *ngIf="!logoCompany?.logoPath" class="text-muted-color text-sm">No logo</div>
                    <input type="file" accept="image/*" (change)="onLogoSelect($event)" class="p-2 border border-surface rounded-border w-full" />
                    <p-button label="Upload Logo" icon="pi pi-upload" size="small" [loading]="logoUploading" [disabled]="!logoFile" (onClick)="uploadLogo()"></p-button>
                    <p-button *ngIf="logoCompany?.logoPath" label="Remove Logo" icon="pi pi-trash" size="small" severity="danger" [outlined]="true" (onClick)="removeLogoImg()"></p-button>
                </div>

                <!-- SIGNATURE -->
                <div class="flex flex-col gap-2 items-center border-b pb-4">
                    <div class="font-medium text-sm self-start">Signature</div>
                    <img *ngIf="logoCompany?.signaturePath" [src]="logoUrl(logoCompany!.signaturePath!)" style="max-height:60px; object-fit:contain; border:1px solid #ddd; padding:6px; border-radius:8px;" />
                    <div *ngIf="!logoCompany?.signaturePath" class="text-muted-color text-sm">No signature</div>
                    <input type="file" accept="image/*" (change)="onSigSelect($event)" class="p-2 border border-surface rounded-border w-full" />
                    <p-button label="Upload Signature" icon="pi pi-upload" size="small" [loading]="sigUploading" [disabled]="!sigFile" (onClick)="uploadSig()"></p-button>
                    <p-button *ngIf="logoCompany?.signaturePath" label="Remove Signature" icon="pi pi-trash" size="small" severity="danger" [outlined]="true" (onClick)="removeSigImg()"></p-button>
                </div>

                <!-- STAMP -->
                <div class="flex flex-col gap-2 items-center">
                    <div class="font-medium text-sm self-start">Stamp</div>
                    <img *ngIf="logoCompany?.stampPath" [src]="logoUrl(logoCompany!.stampPath!)" style="max-height:60px; object-fit:contain; border:1px solid #ddd; padding:6px; border-radius:8px;" />
                    <div *ngIf="!logoCompany?.stampPath" class="text-muted-color text-sm">No stamp</div>
                    <input type="file" accept="image/*" (change)="onStampSelect($event)" class="p-2 border border-surface rounded-border w-full" />
                    <p-button label="Upload Stamp" icon="pi pi-upload" size="small" [loading]="stampUploading" [disabled]="!stampFile" (onClick)="uploadStampImg()"></p-button>
                    <p-button *ngIf="logoCompany?.stampPath" label="Remove Stamp" icon="pi pi-trash" size="small" severity="danger" [outlined]="true" (onClick)="removeStampImg()"></p-button>
                </div>
            </div>
            <ng-template pTemplate="footer">
                <p-button label="Close" severity="secondary" (onClick)="logoDialog = false"></p-button>
            </ng-template>
        </p-dialog>
    `
})
export class Companies implements OnInit {
    companies: Company[] = [];
    loading = false;

    dialog = false;
    editId: number | null = null;
    item: Company = {} as Company;

    // logo
    logoDialog = false;
    logoCompany: Company | null = null;
    logoFile: File | null = null;
    logoUploading = false;
    sigFile: File | null = null;
    sigUploading = false;
    stampFile: File | null = null;
    stampUploading = false;

    constructor(
        private api: ApiService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService,
        private cd: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.load();
    }

    logoUrl(path: string): string {
        return environment.serverUrl + path;
    }

    load(): void {
        this.loading = true;
        this.api.getAllCompanies().subscribe({
            next: (data) => {
                this.companies = data;
                this.loading = false;
                this.cd.detectChanges();
            },
            error: () => {
                this.loading = false;
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load companies.' });
                this.cd.detectChanges();
            }
        });
    }

    openNew(): void {
        this.editId = null;
        this.item = { showDnHeader: true } as Company;
        this.dialog = true;
    }

    openEdit(c: Company): void {
        this.editId = c.id;
        this.item = { ...c };
        this.dialog = true;
    }

    save(): void {
        if (!this.item.name || !this.item.name.trim()) {
            this.messageService.add({ severity: 'warn', summary: 'Wait', detail: 'Company name is required.' });
            return;
        }

        const obs = this.editId ? this.api.updateCompany(this.editId, this.item) : this.api.createCompany(this.item);

        obs.subscribe({
            next: () => {
                this.dialog = false;
                this.messageService.add({ severity: 'success', summary: 'Saved', detail: this.editId ? 'Company updated.' : 'Company created.' });
                this.load();
                this.cd.detectChanges();
            },
            error: (err) => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.message || 'Save failed.' });
                this.cd.detectChanges();
            }
        });
    }

    confirmDelete(c: Company): void {
        this.confirmationService.confirm({
            message: `Deactivate "${c.name}"? The company will be hidden but its records stay safe.`,
            header: 'Confirm Deactivate',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Yes, deactivate',
            rejectLabel: 'Cancel',
            acceptButtonStyleClass: 'p-button-danger',
            accept: () => {
                this.api.deleteCompany(c.id).subscribe({
                    next: (res: any) => {
                        this.messageService.add({ severity: 'success', summary: 'Done', detail: res?.message || 'Deleted.' });
                        this.load();
                        this.cd.detectChanges();
                    },
                    error: () => {
                        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Delete failed.' });
                        this.cd.detectChanges();
                    }
                });
            }
        });
    }

    activate(c: Company): void {
        this.api.activateCompany(c.id).subscribe({
            next: (res: any) => {
                this.messageService.add({ severity: 'success', summary: 'Done', detail: res?.message || 'Company activated.' });
                this.load();
                this.cd.detectChanges();
            },
            error: () => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Activate failed.' });
                this.cd.detectChanges();
            }
        });
    }

    // ---- logo ----
    openLogo(c: Company): void {
        this.logoCompany = c;
        this.logoFile = null;
        this.logoDialog = true;
    }

    onLogoSelect(event: Event): void {
        const input = event.target as HTMLInputElement;
        this.logoFile = input.files && input.files.length ? input.files[0] : null;
    }

    uploadLogo(): void {
        if (!this.logoCompany || !this.logoFile) return;
        this.logoUploading = true;
        this.api.uploadLogo(this.logoCompany.id, this.logoFile).subscribe({
            next: (res: any) => {
                this.logoUploading = false;
                this.logoDialog = false;
                this.messageService.add({ severity: 'success', summary: 'Done', detail: 'Logo uploaded.' });
                this.load();
                this.cd.detectChanges();
            },
            error: (err) => {
                this.logoUploading = false;
                this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.message || 'Logo upload failed.' });
                this.cd.detectChanges();
            }
        });
    }

    onSigSelect(e: Event): void {
        const i = e.target as HTMLInputElement;
        this.sigFile = i.files?.length ? i.files[0] : null;
    }
    onStampSelect(e: Event): void {
        const i = e.target as HTMLInputElement;
        this.stampFile = i.files?.length ? i.files[0] : null;
    }

    uploadSig(): void {
        if (!this.logoCompany || !this.sigFile) return;
        this.sigUploading = true;
        this.api.uploadSignature(this.logoCompany.id, this.sigFile).subscribe({
            next: (res: any) => {
                this.sigUploading = false;
                this.messageService.add({ severity: 'success', summary: 'Done', detail: 'Signature uploaded.' });
                if (this.logoCompany) this.logoCompany.signaturePath = res?.path;
                this.sigFile = null;
                this.load();
                this.cd.detectChanges();
            },
            error: () => {
                this.sigUploading = false;
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Upload failed.' });
                this.cd.detectChanges();
            }
        });
    }

    uploadStampImg(): void {
        if (!this.logoCompany || !this.stampFile) return;
        this.stampUploading = true;
        this.api.uploadStamp(this.logoCompany.id, this.stampFile).subscribe({
            next: (res: any) => {
                this.stampUploading = false;
                this.messageService.add({ severity: 'success', summary: 'Done', detail: 'Stamp uploaded.' });
                if (this.logoCompany) this.logoCompany.stampPath = res?.path;
                this.stampFile = null;
                this.load();
                this.cd.detectChanges();
            },
            error: () => {
                this.stampUploading = false;
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Upload failed.' });
                this.cd.detectChanges();
            }
        });
    }

    removeLogo(): void {
        if (!this.logoCompany) return;
        this.api.removeLogo(this.logoCompany.id).subscribe({
            next: (res: any) => {
                this.messageService.add({ severity: 'success', summary: 'Done', detail: res?.message || 'Logo removed.' });
                if (this.logoCompany) this.logoCompany.logoPath = undefined;
                this.logoFile = null;
                this.load();
                this.cd.detectChanges();
            },
            error: () => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not remove logo.' });
                this.cd.detectChanges();
            }
        });
    }

    removeLogoImg(): void {
        if (!this.logoCompany) return;
        this.api.removeLogo(this.logoCompany.id).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Done', detail: 'Logo removed.' });
                if (this.logoCompany) this.logoCompany.logoPath = undefined;
                this.load();
                this.cd.detectChanges();
            },
            error: () => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Remove failed.' });
                this.cd.detectChanges();
            }
        });
    }

    removeSigImg(): void {
        if (!this.logoCompany) return;
        this.api.removeSignature(this.logoCompany.id).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Done', detail: 'Signature removed.' });
                if (this.logoCompany) this.logoCompany.signaturePath = undefined;
                this.load();
                this.cd.detectChanges();
            },
            error: () => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Remove failed.' });
                this.cd.detectChanges();
            }
        });
    }

    removeStampImg(): void {
        if (!this.logoCompany) return;
        this.api.removeStamp(this.logoCompany.id).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Done', detail: 'Stamp removed.' });
                if (this.logoCompany) this.logoCompany.stampPath = undefined;
                this.load();
                this.cd.detectChanges();
            },
            error: () => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Remove failed.' });
                this.cd.detectChanges();
            }
        });
    }
}
